/**
 * ForgotPasswordPage — WO5 user-data isolation batch.
 *
 * Single email field + submit. ALWAYS shows the same generic confirmation
 * regardless of whether the email exists in our system. The backend's
 * identity-leak protection is mirrored here on the client so users never
 * see different copy for known-vs-unknown emails.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { apiV1Base } from '../lib/apiConfig';

const GENERIC_CONFIRMATION =
  "If an account exists for that email, we've sent a reset link. The link expires in 1 hour.";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setLocalError('Enter your email.');
      return;
    }

    setLoading(true);
    try {
      // We don't read the response body for content reasons — the backend
      // returns the same generic message in every case. We only care that
      // the call completed (any HTTP status is fine, we still show generic).
      await fetch(`${apiV1Base()}/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
    } catch {
      // Network failure also resolves to generic — never reveal anything.
    }
    setLoading(false);
    setDone(true);
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        background: 'var(--app-bg, #07070b)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--card-bg, #111118)',
          border: '1px solid var(--card-border, #1f1f2c)',
          borderRadius: 14,
          padding: '1.75rem',
          color: 'var(--app-text, #e2e2e8)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/login')}
          data-testid="forgot-password-back"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--muted-text, #6b6b80)',
            fontSize: 12,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: 0,
            marginBottom: '1rem',
          }}
        >
          <ArrowLeft size={12} /> Back to sign in
        </button>

        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            margin: '0 0 0.4rem 0',
            letterSpacing: '-0.01em',
          }}
        >
          Reset your password
        </h1>
        <p
          style={{
            fontSize: 13,
            color: 'var(--muted-text, #6b6b80)',
            margin: '0 0 1.5rem 0',
            lineHeight: 1.5,
          }}
        >
          Enter the email on your Adversyn account. If it matches, we'll
          send you a link to set a new password.
        </p>

        {done ? (
          <div
            data-testid="forgot-password-confirmation"
            style={{
              padding: '1rem 1.1rem',
              background: 'rgba(16,185,129,0.07)',
              border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 10,
              fontSize: 13,
              lineHeight: 1.55,
              color: 'var(--app-text, #e2e2e8)',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <CheckCircle2
              size={18}
              color="#10b981"
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <span>{GENERIC_CONFIRMATION}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label
              style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--muted-text, #6b6b80)',
                marginBottom: 6,
              }}
            >
              Email
            </label>
            <div
              style={{
                position: 'relative',
                marginBottom: '1.25rem',
              }}
            >
              <Mail
                size={14}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted-text, #6b6b80)',
                }}
              />
              <input
                data-testid="forgot-password-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={{
                  width: '100%',
                  padding: '0.7rem 0.85rem 0.7rem 2.2rem',
                  fontSize: 14,
                  background: 'var(--input-bg, #0d0d14)',
                  border: '1px solid var(--input-border, #25253a)',
                  borderRadius: 8,
                  color: 'var(--app-text, #e2e2e8)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {localError && (
              <div
                data-testid="forgot-password-error"
                style={{
                  marginBottom: '1rem',
                  padding: '0.55rem 0.8rem',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#fda4a4',
                }}
              >
                {localError}
              </div>
            )}

            <button
              type="submit"
              data-testid="forgot-password-submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.7rem 1rem',
                fontSize: 13,
                fontWeight: 800,
                background: '#10b981',
                color: '#06170f',
                border: 'none',
                borderRadius: 8,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: loading ? 0.8 : 1,
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Sending reset link...
                </>
              ) : (
                <>
                  Send reset link
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
